import React from 'react';
import { alpha, Box, IconButton, Tooltip } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import dayjs from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { getCurrentYearMonth, isPreviousYear, shiftYearMonth } from '../util/TimeUtil';
import type { DashboardStatus } from '../types/DashboardType';

export const MIN_YEAR_MONTH = '2020-01';

type ChipColors = { border: string; background: string; text: string };

// Soft chip colours: a pale tint with darker text (readable at 4.5:1+) and a coloured border.
// The keys are what saved filters store (same list as the backend SAVED_FILTER_COLORS).
export const CHIP_COLORS: Record<string, ChipColors> = {
  blue: { border: '#5b9bd9', background: '#e6f0fa', text: '#2f6aa8' },
  green: { border: '#6aa86d', background: '#e9f3ea', text: '#3a7a3e' },
  amber: { border: '#d9a441', background: '#fbf1dc', text: '#7f5600' },
  red: { border: '#d97a7a', background: '#fbe9e9', text: '#a33a3a' },
  purple: { border: '#9b82c9', background: '#f0ebf8', text: '#5e4594' },
  slate: { border: '#8a97a6', background: '#eef1f4', text: '#4a5563' },
};

// Pending and Not reporting keep the default grey chip in the table.
const STATUS_COLORS: Partial<Record<DashboardStatus, ChipColors>> = {
  READY_TO_START: CHIP_COLORS.blue,
  COMPLETE: CHIP_COLORS.green,
};

/** Chip colours for a status: tinted when filled (selected / highlighted), white with coloured border otherwise. */
export function statusChipSx(status: DashboardStatus, filled: boolean) {
  return chipColorSx(STATUS_COLORS[status], filled);
}

/** Chip styles for one of the CHIP_COLORS: tinted when filled, white with coloured border and text otherwise. */
export function chipColorSx(colors: ChipColors | undefined, filled: boolean) {
  if (!colors) return {};
  const background = filled ? colors.background : '#fff';
  // Hover is set too, otherwise MUI's grey hover background replaces the colour on clickable chips.
  return {
    backgroundColor: background,
    color: colors.text,
    border: `1px solid ${colors.border}`,
    '&:hover': { backgroundColor: filled ? alpha(colors.border, 0.22) : colors.background },
  };
}

export function MonthStepper(props: { yearMonth: string; onChange: (yearMonth: string) => void }) {
  const { yearMonth, onChange } = props;
  const previous = shiftYearMonth(yearMonth, -1);
  const next = shiftYearMonth(yearMonth, 1);
  const canGoBack = previous >= MIN_YEAR_MONTH;
  const canGoForward = next <= getCurrentYearMonth();

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Tooltip title={canGoBack ? dayjs(previous, 'YYYY-MM').format('MMMM YYYY') : ''}>
        <span>
          <IconButton size="small" aria-label="Previous month" disabled={!canGoBack} onClick={() => onChange(previous)}>
            <ChevronLeftIcon />
          </IconButton>
        </span>
      </Tooltip>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          sx={{
            width: '200px',
            '.MuiInputBase-input': { paddingTop: '5px', paddingBottom: '5px', fontWeight: 600 },
          }}
          value={dayjs(yearMonth, 'YYYY-MM')}
          format="MMMM YYYY"
          minDate={dayjs(MIN_YEAR_MONTH, 'YYYY-MM')}
          maxDate={dayjs()}
          disableFuture
          views={['month', 'year']}
          slotProps={{ field: { readOnly: true }, textField: { inputProps: { 'aria-label': 'Reporting month' } } }}
          onChange={newValue => {
            if (newValue && newValue.isValid()) {
              const newYearMonth = newValue.format('YYYY-MM');
              // Switching to the previous year from the year view lands on December, the usual end of quarter to review.
              onChange(isPreviousYear(yearMonth, newYearMonth) ? newYearMonth.substring(0, 5) + '12' : newYearMonth);
            }
          }}
        />
      </LocalizationProvider>
      <Tooltip title={canGoForward ? dayjs(next, 'YYYY-MM').format('MMMM YYYY') : ''}>
        <span>
          <IconButton size="small" aria-label="Next month" disabled={!canGoForward} onClick={() => onChange(next)}>
            <ChevronRightIcon />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
}
